<script setup lang="ts">
/// <reference types="@types/google.maps" />

interface PlaceSelectedData {
  address: string;
  lat: number;
  lng: number;
  name?: string;
}

interface PlacePredictionLike {
  text: { toString(): string };
  placeId: string;
  toPlace(): {
    fetchFields(opts: { fields: string[] }): Promise<void>;
    location?: { lat(): number; lng(): number } | null;
    formattedAddress?: string | null;
    displayName?: string | null;
  };
}

interface PredictionItem {
  title: string;
  value: string;
  prediction: PlacePredictionLike;
}

defineProps<{
  modelValue?: string;
  disabled?: boolean;
  errorMessages?: string | string[];
  rules?: ((value: unknown) => true | string)[];
  label?: string;
  hint?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'place-selected': [data: PlaceSelectedData];
}>();

const config = useRuntimeConfig();
const apiKey = config.public.googleMapsApiKey as string;

const placesReady = ref(false);
const items = ref<PredictionItem[]>([]);
const loading = ref(false);

let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function renewSessionToken() {
  if (placesReady.value) {
    sessionToken = new google.maps.places.AutocompleteSessionToken();
  }
}

onMounted(async () => {
  if (!apiKey) return;

  try {
    const { setOptions, importLibrary } =
      await import('@googlemaps/js-api-loader');
    setOptions({ key: apiKey });
    await importLibrary('places');
    placesReady.value = true;
    renewSessionToken();
  } catch {
    // Graceful degradation — fall back to plain text field
  }
});

onUnmounted(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
});

async function fetchPredictions(query: string) {
  if (!placesReady.value || query.length < 3) {
    items.value = [];
    return;
  }

  loading.value = true;
  try {
    const request: google.maps.places.AutocompleteRequest = {
      input: query,
      includedRegionCodes: ['us', 'ca', 'mx'],
      sessionToken: sessionToken ?? undefined,
    };

    const { suggestions } =
      await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        request,
      );

    items.value = suggestions
      .filter(
        (s): s is typeof s & { placePrediction: PlacePredictionLike } =>
          !!s.placePrediction,
      )
      .map((s) => ({
        title: s.placePrediction.text.toString(),
        value: s.placePrediction.placeId,
        prediction: s.placePrediction,
      }));
  } catch {
    items.value = [];
  } finally {
    loading.value = false;
  }
}

function onSearchUpdate(query: string | null) {
  if (debounceTimer) clearTimeout(debounceTimer);

  if (!query || query.length < 3) {
    items.value = [];
    return;
  }

  debounceTimer = setTimeout(() => {
    fetchPredictions(query);
  }, 300);
}

async function onItemSelected(selected: string | PredictionItem | null) {
  if (!selected || typeof selected === 'string') {
    emit('update:modelValue', selected ?? '');
    return;
  }

  emit('update:modelValue', selected.title);

  try {
    const place = selected.prediction.toPlace();
    await place.fetchFields({
      fields: ['formattedAddress', 'location', 'displayName'],
    });

    const location = place.location;
    if (location) {
      emit('place-selected', {
        address: place.formattedAddress ?? selected.title,
        lat: location.lat(),
        lng: location.lng(),
        name: place.displayName ?? undefined,
      });
      emit(
        'update:modelValue',
        place.displayName ?? place.formattedAddress ?? selected.title,
      );
    }
  } catch {
    // Place details failed — keep the text selection as-is
  }

  renewSessionToken();
  items.value = [];
}

function onFallbackInput(value: string) {
  emit('update:modelValue', value);
}
</script>

<template>
  <v-combobox
    v-if="placesReady"
    :model-value="modelValue"
    :items="items"
    item-title="title"
    item-value="value"
    return-object
    no-filter
    :loading="loading"
    :label="label"
    :hint="hint"
    :disabled="disabled"
    :error-messages="errorMessages"
    :rules="rules"
    clearable
    class="mb-2"
    @update:search="onSearchUpdate"
    @update:model-value="onItemSelected"
  />
  <v-text-field
    v-else
    :model-value="modelValue"
    :label="label"
    :hint="hint"
    :disabled="disabled"
    :error-messages="errorMessages"
    :rules="rules"
    class="mb-2"
    @update:model-value="onFallbackInput"
  />
</template>
